import { createTheme } from '@mui/material/styles';
import logoLight from '../images/logos/logo-light-theme.png';
import logoDark from '../images/logos/logo-dark-theme.png';
import cloudLogoLight from '../images/environments/cloud-dark.png';
import cloudLogoDark from '../images/environments/cloud-light.png';

// Shared values used across themes
const fontFamilies = {
  primary: '"Lexend Variable", Arial',
  secondary: 'Kanit',
  monospace: 'Roboto Condensed',
  // Display family for hero numbers / large KPIs. Lexend reads well at
  // big sizes; we tighten letter-spacing on the dashboard KPI surface
  // for the same vibe as the marketing site.
  display: '"Lexend Variable", Arial',
};

// Brand accents shared across themes. Mirror what's on the marketing
// site (predict.ai) so the in-product surface feels like the same
// product. Lime is the primary accent; teal is a calm secondary used
// for trust + data viz.
const brand = {
  lime: '#c0fb6e',
  limeDark: '#85d818',
  teal: '#197563',
  ink:  '#0a0c0a',
};

// Font configurations
const typography = {
  fontFamily: fontFamilies.primary,
  fontSize: 14,
  h1: {
    fontFamily: fontFamilies.primary,
    fontSize: '32px',
    fontWeight: 500,
  },
  h2: {
    fontFamily: fontFamilies.primary,
    fontSize: '24px',
    fontWeight: 500,
  },
  h3: {
    fontFamily: fontFamilies.primary,
    fontSize: '20px',
    fontWeight: 500,
  },
  h4: {
    fontFamily: fontFamilies.primary,
    fontSize: '14px',
    fontWeight: 600,
  },
  body1: {
    fontFamily: fontFamilies.primary,
    fontSize: '14px',
  },
  body2: {
    fontFamily: fontFamilies.primary,
    fontSize: '12px',
  },
};

// Shape / border-radius config.
// Cards / Papers use ``cardBorderRadius`` (4) - tight, modern look.
// Other surfaces (TextField, MenuItem, Tooltip, etc.) keep the
// slightly softer global ``borderRadius`` (6). Buttons stay on their
// pronounced pill radius (18) - that's a deliberate brand choice.
//
// The Card+Paper override is applied via component styleOverrides
// below so every Card, Paper(rounded), styled-Paper, etc. picks up
// the smaller radius automatically without per-call edits.
const shape = {
  borderRadius: 8,
  buttonBorderRadius: 18,
  cardBorderRadius: 8,
};

// Shared component styles between themes
const commonComponents = {
  MuiCssBaseline: {
    styleOverrides: `
      @font-face {
        font-family: '${fontFamilies.primary}';
        font-style: normal;
        font-display: swap;
        font-weight: 400;
      }
    `,
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontFamily: fontFamilies.primary,
        borderRadius: shape.buttonBorderRadius,
        fontWeight: 'bold',
        padding: '6px 12px',
        height: 40,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      },
      sizeSmall: {
        height: 32,
        padding: '4px 10px',
      },
    }
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        fontFamily: fontFamilies.primary,
      }
    }
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        marginBottom: '6px'
      }
    }
  },
  // Cards / Papers system-wide use the smaller cardBorderRadius.
  // - MuiCard.root          : every <Card />
  // - MuiPaper.rounded      : every <Paper /> that doesn't pass
  //                            ``square``. Menus/Popovers inherit too,
  //                            which is intentional - they look better
  //                            consistent with cards.
  // ``square=true`` Papers (AppBar, Drawer surface, etc.) are
  // unaffected because the override is on the ``rounded`` slot.
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: shape.cardBorderRadius,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: shape.cardBorderRadius,
      },
    },
  },
};

// Node colors for light theme
const lightNodeColors = {
  input: '#2196F3', // Blue
  output: '#4CAF50', // Green
  dense: '#9C27B0', // Purple
  dropout: '#FF9800', // Orange
  lstm: '#E91E63', // Pink
  gru: '#673AB7', // Deep Purple
  rnn: '#3F51B5', // Indigo
  attention: '#FFC107', // Amber
  multiheadattention: '#FF5722', // Deep Orange
  conv1d: '#F44336', // Red
  conv2d: '#795548', // Brown
  maxpooling: '#607D8B', // Blue Grey
  batchnormalization: '#009688', // Teal
  embedding: '#8BC34A', // Light Green
  add: '#CDDC39', // Lime
  concatenate: '#00BCD4', // Cyan
  flatten: '#03A9F4', // Light Blue
};

// Node text colors for light theme
const lightNodeTextColors = {
  input: '#FFFFFF', // White for contrast on blue
  output: '#FFFFFF', // White for contrast on green
  dense: '#FFFFFF', // White for contrast on purple
  dropout: '#000000', // Black for contrast on orange
  lstm: '#FFFFFF', // White for contrast on pink
  gru: '#FFFFFF', // White for contrast on deep purple
  rnn: '#FFFFFF', // White for contrast on indigo
  attention: '#000000', // Black for contrast on amber
  multiheadattention: '#FFFFFF', // White for contrast on deep orange
  conv1d: '#FFFFFF', // White for contrast on red
  conv2d: '#FFFFFF', // White for contrast on brown
  maxpooling: '#FFFFFF', // White for contrast on blue grey
  batchnormalization: '#FFFFFF', // White for contrast on teal
  embedding: '#000000', // Black for contrast on light green
  add: '#000000', // Black for contrast on lime
  concatenate: '#000000', // Black for contrast on cyan
  flatten: '#000000', // Black for contrast on light blue
};

// Node colors for dark theme
const darkNodeColors = {
  input: '#1976D2', // Darker Blue
  output: '#388E3C', // Darker Green
  dense: '#7B1FA2', // Darker Purple
  dropout: '#F57C00', // Darker Orange
  lstm: '#C2185B', // Darker Pink
  gru: '#512DA8', // Darker Deep Purple
  rnn: '#303F9F', // Darker Indigo
  attention: '#FFA000', // Darker Amber
  multiheadattention: '#E64A19', // Darker Deep Orange
  conv1d: '#D32F2F', // Darker Red
  conv2d: '#5D4037', // Darker Brown
  maxpooling: '#455A64', // Darker Blue Grey
  batchnormalization: '#00796B', // Darker Teal
  embedding: '#689F38', // Darker Light Green
  add: '#AFB42B', // Darker Lime
  concatenate: '#0097A7', // Darker Cyan
  flatten: '#0288D1', // Darker Light Blue
};

// Node text colors for dark theme (mostly the same as light theme)
const darkNodeTextColors = {
  ...lightNodeTextColors,
};

// Data type colors for light theme
const lightDataTypeColors = {
  continuous_numerical: '#4CAF50', // success green
  discrete_numerical: '#4CAF50', // success green
  ordinal_categorical: '#FF9800', // warning orange
  nominal_categorical: '#FF9800', // warning orange
  binary_boolean: '#2196F3', // info blue
  categorical_text: '#FF9800', // warning orange
  free_text_unstructured: '#9E9E9E', // secondary grey
  structured_text_codes: '#1976D2', // primary blue
  datetime_timestamp: '#2196F3', // info blue
  geospatial_coordinates: '#F44336', // error red
  unique_identifiers: '#757575', // default grey
  mixed_complex: '#757575', // default grey
};

// Data type background colors for light theme (lighter versions)
const lightDataTypeBackgrounds = {
  continuous_numerical: 'rgba(76, 175, 80, 0.1)',
  discrete_numerical: 'rgba(76, 175, 80, 0.1)',
  ordinal_categorical: 'rgba(255, 152, 0, 0.1)',
  nominal_categorical: 'rgba(255, 152, 0, 0.1)',
  binary_boolean: 'rgba(33, 150, 243, 0.1)',
  categorical_text: 'rgba(255, 152, 0, 0.1)',
  free_text_unstructured: 'rgba(158, 158, 158, 0.1)',
  structured_text_codes: 'rgba(25, 118, 210, 0.1)',
  datetime_timestamp: 'rgba(33, 150, 243, 0.1)',
  geospatial_coordinates: 'rgba(244, 67, 54, 0.1)',
  unique_identifiers: 'rgba(117, 117, 117, 0.1)',
  mixed_complex: 'rgba(117, 117, 117, 0.1)',
};

// Data type colors for dark theme
const darkDataTypeColors = {
  continuous_numerical: '#66BB6A', // lighter success green
  discrete_numerical: '#66BB6A', // lighter success green
  ordinal_categorical: '#FFB74D', // lighter warning orange
  nominal_categorical: '#FFB74D', // lighter warning orange
  binary_boolean: '#64B5F6', // lighter info blue
  categorical_text: '#FFB74D', // lighter warning orange
  free_text_unstructured: '#BDBDBD', // lighter secondary grey
  structured_text_codes: '#42A5F5', // lighter primary blue
  datetime_timestamp: '#64B5F6', // lighter info blue
  geospatial_coordinates: '#EF5350', // lighter error red
  unique_identifiers: '#9E9E9E', // lighter default grey
  mixed_complex: '#9E9E9E', // lighter default grey
};

// Data type background colors for dark theme
const darkDataTypeBackgrounds = {
  continuous_numerical: 'rgba(102, 187, 106, 0.2)',
  discrete_numerical: 'rgba(102, 187, 106, 0.2)',
  ordinal_categorical: 'rgba(255, 183, 77, 0.2)',
  nominal_categorical: 'rgba(255, 183, 77, 0.2)',
  binary_boolean: 'rgba(100, 181, 246, 0.2)',
  categorical_text: 'rgba(255, 183, 77, 0.2)',
  free_text_unstructured: 'rgba(189, 189, 189, 0.2)',
  structured_text_codes: 'rgba(66, 165, 245, 0.2)',
  datetime_timestamp: 'rgba(100, 181, 246, 0.2)',
  geospatial_coordinates: 'rgba(239, 83, 80, 0.2)',
  unique_identifiers: 'rgba(158, 158, 158, 0.2)',
  mixed_complex: 'rgba(158, 158, 158, 0.2)',
};

// Theme definitions
export const lightTheme = createTheme({
  typography,
  shape,
  components: commonComponents,
  logo: logoLight,
  brand,
  fontFamilies,
  environments:{
    cloud: cloudLogoLight,
  },
  nodeColors: lightNodeColors,
  nodeTextColors: lightNodeTextColors,
  dataTypeColors: lightDataTypeColors,
  dataTypeBackgrounds: lightDataTypeBackgrounds,
  palette: {
    mode: 'light',
    background: {
      default: '#f9f9f9',
      paper: '#ffffff',
      sidebar: '#ffffff',
      // Light counterpart of the dark sidebarHover: one step off-white.
      sidebarHover: '#f3f5f4',
      header: '#f1f0ee',
      card: '#ebe9e5',
      cardHover: '#f4f4f4',
      cardStatic: '#fefefe',
      inputDark: '#f7f7f7',
      inputDarkHover: '#f0f0f0',
      generativeEnabled: 'rgba(97, 77, 238, 0.08)',
    },
    primary: {
      main: '#197563',
      light: '#eaeeff',
      dark: '#2C2C2C',
      contrastText: '#fff',
    },
    secondary: {
      main: '#42405d',
      light: '#b4fe8c',
      dark: '#323048',
      contrastText: '#fff',
    },
    success: {
      main: '#2f7d31',
      light: '#98db9b',
      dark: '#1d621f',
      contrastText: '#fff',
    },
    error: {
      main: '#d83f3f',
      light: '#f8acac',
      dark: '#962b2b',
      contrastText: '#fff',
    },
    validation: {
      main: '#fff6f6',
      light: '#fff6f6',
      dark: '#dc5352',
      border: '#c5cae9',
      contrastText: '#dc5352',
    },
    text: {
      primary: '#2C2C2C',
      secondary: '#2C2C2C',
      disabled: '#c1c7c6',
      highlight: '#197563',
      darkHighlight: '#000000',
    },
    divider: '#e8e8e8',
    action: {
      active: '#2C2C2C',
      hover: '#b4fe8c',
    },
    // Additional UI elements
    boxShadow: '0 0px 8px rgba(0, 0, 0, 0.1)',
    lightBoxShadow: '0 0px 4px rgba(0, 0, 0, 0.08)',
    schedule: {
      daySelected: '#2f7d31',
      daySelectedHover: '#1d621f',
      dayText: '#fff',
      dayBackground: 'transparent',
      dayHover: 'rgba(47, 125, 49, 0.08)'
    },
  },
});

export const darkTheme = createTheme({
  typography,
  shape,
  components: {
    ...commonComponents,
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: '${fontFamilies.primary}';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
        }
        body {
          background-color: #121212;
        }
        pre {
          background-color: #1e1e1e !important;
          padding: 16px;
          border-radius: 6px;
          overflow-x: auto;
        }
      `,
    },
  },
  logo: logoDark,
  brand,
  fontFamilies,
  environments:{
    cloud: cloudLogoDark,
  },
  nodeColors: darkNodeColors,
  nodeTextColors: darkNodeTextColors,
  dataTypeColors: darkDataTypeColors,
  dataTypeBackgrounds: darkDataTypeBackgrounds,
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1e1e1e',
      sidebar: '#191a1b',
      // Shared surface for the goals flyout panel and the hovered Goals row,
      // one step lighter than the rail so the pair reads as a single layer.
      sidebarHover: '#232527',
      header: '#232323',
      card: '#212121',
      cardHover: '#191919',
      cardStatic: '#212121',
      inputDark: '#212121',
      inputDarkHover: '#333333',
      generativeEnabled: 'rgba(137, 116, 255, 0.1)',
    },
    primary: {
      main: '#c0fb6e',
      light: '#2c331f',
      dark: '#c0fb6e',
      contrastText: '#000',
    },
    secondary: {
      main: '#a0a0a0',
      light: '#c8c8c8',
      dark: '#7a7a7a',
      contrastText: '#fff',
    },
    success: {
      main: '#4caf50',
      light: '#80e27e',
      dark: '#087f23',
      contrastText: '#fff',
    },
    error: {
      main: '#f44336',
      light: '#ff7961',
      dark: '#ba000d',
      contrastText: '#fff',
    },
    validation: {
      main: '#fff6f6',
      light: '#fff6f6',
      dark: '#dc5352',
      border: '#2C2C2C',
      contrastText: '#fff',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
      disabled: '#6c6c6c',
      highlight: '#c0fb6e',
      darkHighlight: '#000000',
    },
    divider: '#404040',
    action: {
      active: '#fff',
      hover: '#242a18',
    },
    // Additional UI elements
    boxShadow: '0 0px 8px rgba(0, 0, 0, 0.5)',
    lightBoxShadow: '0 0px 4px rgba(0, 0, 0, 0.3)',
    generative: {
      main: '#2C2C2C',
      light: '#2C2C2C',
      dark: '#2C2C2C',
      contrastText: '#fff',
      switchTrack: '#2C2C2C',
      switchThumb: '#2C2C2C',
      switchTrackOff: '#666666',
      switchThumbOff: '#999999'
    },
    schedule: {
      daySelected: '#4caf50',
      daySelectedHover: '#087f23',
      dayText: '#fff',
      dayBackground: 'transparent',
      dayHover: 'rgba(76, 175, 80, 0.15)'
    },
  },
});

export default lightTheme; 